import React from "react";
import { Box } from "@mui/material";
import { ConfirmDialog } from "@/shared/ui/ConfirmDialog";
import {
  VendorStepper,
  VendorSelector,
  CredentialsForm,
  ConnectedSuccessView,
  LoadingSkeleton,
} from "../../pages/ConnectBodycam/components";
import type { Vendor } from "../../pages/ConnectBodycam/vendorConfig";
import { useBodycamOnboarding } from "../../pages/ConnectBodycam/hooks";

export interface BodycamOnboardingPanelProps {
  stationId: string;
  onReturnHome?: () => void;
  initialVendorAlreadyOnboarded?: Vendor | null;
  onVendorChange?: (vendor: Vendor | null) => void;
}

const BodycamOnboardingPanel: React.FC<BodycamOnboardingPanelProps> = ({
  stationId,
  onReturnHome,
  initialVendorAlreadyOnboarded,
  onVendorChange,
}) => {
  const {
    activeStep,
    selectedVendor,
    isLoading,
    success,
    vendorAlreadyOnboarded,
    isRestarting,
    isRemoveDialogOpen,
    isRemoving,
    handleSelectVendor,
    handleContinue,
    handleBack,
    handleCredentialsSuccess,
    startUpdateFlow,
    setIsRemoveDialogOpen,
    handleRemoveIntegration,
  } = useBodycamOnboarding(stationId, { initialVendorAlreadyOnboarded, onVendorChange });

  const currentVendor = (vendorAlreadyOnboarded as Vendor) ?? selectedVendor;
  const showSuccessView = (success || vendorAlreadyOnboarded) && !isRestarting;

  if (isLoading && vendorAlreadyOnboarded === undefined && !success) {
    return <LoadingSkeleton />;
  }

  return (
    <Box>
      {showSuccessView && currentVendor ? (
        <>
          <ConfirmDialog
            open={isRemoveDialogOpen}
            onClose={() => setIsRemoveDialogOpen(false)}
            onConfirm={handleRemoveIntegration}
            title="Remove bodycam vendor?"
            description="This will remove your current vendor connection. Officers will not be able to sync evidence until you reconnect. Do you want to continue?"
            tone="error"
            loading={isRemoving}
            confirmLabel="Remove"
            cancelLabel="Cancel"
          />

          <ConnectedSuccessView
            vendor={currentVendor}
            onReturnHome={onReturnHome}
            onUpdateClick={() => startUpdateFlow(currentVendor)}
            onRemoveClick={() => setIsRemoveDialogOpen(true)}
          />
        </>
      ) : (
        <>
          <VendorStepper activeStep={activeStep} />

          {activeStep === 0 && (
            <VendorSelector
              selectedVendor={selectedVendor}
              onSelect={handleSelectVendor}
              onContinue={handleContinue}
              isLoading={isLoading}
            />
          )}

          {activeStep === 1 && selectedVendor && (
            <CredentialsForm
              key={selectedVendor}
              vendor={selectedVendor}
              isUpdate={!!vendorAlreadyOnboarded}
              onBack={handleBack}
              onSuccess={handleCredentialsSuccess}
              stationId={stationId}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default BodycamOnboardingPanel;
